import React from 'react';
import { Trash2 } from 'lucide-react';
import { useValidatedTextarea } from '../hooks/useValidatedTextarea';
import clsx from 'clsx';

interface UploadedFileItemProps {
  file: File;
  preview: string;
  generatedFileName: string;
  description: string;
  id: string;
  onDescriptionChange: (id: string, description: string, isValid: boolean) => void;
  onRemove: (id: string) => void;
  disabled?: boolean;
}

export function UploadedFileItem({
  file,
  preview,
  generatedFileName,
  description,
  id,
  onDescriptionChange,
  onRemove,
  disabled = false
}: UploadedFileItemProps) {
  const {
    value,
    wordCount,
    error,
    isValid,
    onChange,
    setValue
  } = useValidatedTextarea({
    initialValue: description,
    maxWords: 250,
    maxChars: 2500,
    maxPunctuationRatio: 0.5,
    maxConsecutivePunctuation: 20,
    allowLatexCounting: true,
    required: false
  });

  // Update parent component when description changes
  React.useEffect(() => {
    onDescriptionChange(id, value, isValid);
  }, [value, isValid, id, onDescriptionChange]);

  // Sync with external description changes
  React.useEffect(() => {
    if (description !== value) {
      setValue(description);
    }
  }, [description, setValue]);

  return (
    <div className="flex flex-col sm:flex-row items-start bg-[--bg-secondary] p-4 rounded-lg shadow-sm">
      <div className="flex-shrink-0 flex flex-col items-center w-24 sm:w-32 mr-4">
        <div className="w-24 h-24 sm:w-32 sm:h-32 overflow-hidden rounded-lg border border-[--border-secondary]">
          <img 
            src={preview} 
            alt={`Preview ${generatedFileName}`} 
            className="w-full h-full object-cover"
          />
        </div>
        <span className="text-xs text-[--text-secondary] mt-2">
          {(file.size / 1024).toFixed(1)} KB
        </span>
      </div>
      <div className="flex-grow mt-4 sm:mt-0 w-full flex flex-col justify-between min-h-[6rem] sm:min-h-[8rem]">
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="text-xs font-medium text-[--text-primary]">
              Image Description
            </label>
            <span className={clsx(
              "text-xs font-medium",
              wordCount > 250 ? "text-error-600" : 
              wordCount > 225 ? "text-warning-600" : 
              "text-[--text-secondary]"
            )}>
              {wordCount}/250 words
            </span>
          </div>
        </div>
        <div>
          <textarea
            value={value}
            onChange={onChange}
            maxLength={2500}
            placeholder="Describe this image and its relevance to your research..."
            rows={3}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm text-[--text-primary] bg-[--bg-primary] border border-[--border-secondary] rounded-md focus:ring-primary-500 focus:border-primary-500 resize-none disabled:opacity-50"
          />
          {error && (
            <p className="mt-1 text-xs text-error-600">
              {error}
            </p>
          )}
        </div>
      </div>
      <div className="flex-shrink-0 mt-4 sm:mt-0 sm:ml-4">
        <button
          type="button"
          onClick={() => onRemove(id)}
          className="p-2 rounded-full text-error-600 hover:bg-error-50 dark:hover:bg-error-900/20 transition-colors disabled:opacity-50"
          title="Remove image"
          disabled={disabled}
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}